import React, { Fragment, MutableRefObject, useCallback, useEffect, useMemo, useState } from 'react'
import clsx from 'clsx'
import { twMerge } from 'tailwind-merge'
import { connect } from 'react-redux'
import { bindActionCreators, Dispatch } from 'redux'
import Typography from '@mui/material/Typography'
import { GRID_CONFIG } from './index'
import { RenderItemProps } from 'components/ListVirtualized'
import FixedSizeList from 'components/ListVirtualized/FixedSizeList'
import { CircularLoading } from 'components/Loading'
import CollectionCard from 'components/DataCard/CollectionCard'
import { actions, selectors } from './duck'
import Button from 'components/Button'
import { RootActionType, RootState } from 'duck'
import { apiSelectors } from 'duck/ApiDuck/selectors'
import { appSelectors } from 'duck/AppDuck'
import { apiActions } from 'duck/ApiDuck'
import CollectionEditorPanel from 'containers/CollectionEditorPanel/Loadable'
import { usePrevious } from 'utils/Hooks'
import PageTitle from './components/PageTitle'
import TabsOne, { TabOne } from 'components/Tabs/TabsOne'
import InputSearch from 'components/Input/InputSearch'
import { ReduxProps } from 'utils/Types'
import { dialogActions } from 'duck/AppDuck/DialogDuck'
import { Collection } from 'models/ApiModels'
import FloatingActions from './components/FloatingActions'

import styles from './ExplorePage.module.scss'

const CURRENT_MODE = 'collections'

const mapStateToProps = (state: RootState) => ({
  currentUserId: apiSelectors.currentUserId(state),
  mode: selectors.mode(state),
  currentPostId: apiSelectors.currentPostId(state),
  currentCollectionId: apiSelectors.currentCollectionId(state),
  collection: selectors.collection(state),
  collectionListLoading: apiSelectors.loading['collections.list'](state),
  explorePageCollectionList: apiSelectors.explorePageCollectionList(state),
  collectionSearchData: selectors.collectionSearchData(state),
  isAuthorized: appSelectors.isAuthorized(state)
})

const mapDispatchToProps = (dispatch: Dispatch<RootActionType>) =>
  bindActionCreators(
    {
      openPage: actions.openPage,
      addDialog: dialogActions.addDialog,
      listCollection: apiActions.collections.list,
      reloadCollections: actions.collection.reload,
      updateSearch: actions.collection.updateSearch,
      toggleCollectionBookmark: apiActions.collections.toggleCollectionBookmark
    },
    dispatch
  )

export type CollectionListProps = ReduxProps<typeof mapStateToProps, typeof mapDispatchToProps> & {
  isGridInViewport: boolean
  isMediumscreenAbove?: boolean
  isSmallscreenAbove?: boolean
  scrollRef?: MutableRefObject<HTMLDivElement | null>
}

const CollectionList: React.FC<CollectionListProps> = props => {
  const {
    reloadCollections,
    openPage,
    listCollection,
    toggleCollectionBookmark,
    updateSearch,
    addDialog,

    currentPostId,
    currentCollectionId,
    collection,
    currentUserId,
    collectionListLoading,
    collectionSearchData,
    mode,
    explorePageCollectionList,
    scrollRef,
    isAuthorized,
    isGridInViewport,
    isMediumscreenAbove,
    isSmallscreenAbove
  } = props

  const { search } = collectionSearchData
  const [modeState, setModeState] = useState('')
  const explorePageCollectionListLength = explorePageCollectionList.length
  const hasExplorePageCollectionList = Boolean(explorePageCollectionListLength)
  const { showSearch } = collectionSearchData
  const { listParams, listType, loaded } = collection
  const prevMode = usePrevious(mode) || ''
  const isCurrentMode = mode === CURRENT_MODE
  const isCurrentModeState = modeState === CURRENT_MODE
  const shouldShowNoResult =
    listParams.search && !hasExplorePageCollectionList && !collectionListLoading

  const loadMoreCollection = useCallback(() => {
    listCollection({
      param: listParams,
      listType
    })
  }, [listParams, listType, listCollection])

  const onClickCollection = useCallback(
    (id: number) => {
      openPage({ id })
    },
    [openPage]
  )
  const onClickBack = useCallback(() => {
    openPage({ id: undefined })
  }, [openPage])

  const renderItem = useCallback(
    (props: RenderItemProps<Collection>) => {
      const { index } = props

      if (!explorePageCollectionList[index]) return null

      const item = explorePageCollectionList[index]

      const collectionId = item.id

      return (
        <CollectionCard
          addDialog={addDialog}
          onClick={onClickCollection}
          currentUserId={currentUserId}
          key={collectionId}
          collection={item}
          toggleCollectionBookmark={toggleCollectionBookmark}
          isAuthorized={isAuthorized}
        />
      )
    },
    [
      explorePageCollectionList,
      isAuthorized,
      currentUserId,
      addDialog,
      onClickCollection,
      toggleCollectionBookmark
    ]
  )

  const classes = useMemo(() => {
    return {
      gridContainer: clsx(styles.GridImages, isCurrentModeState && styles.GridImagesShown)
    }
  }, [isCurrentModeState])

  useEffect(() => {
    if (isCurrentMode && !loaded) {
      reloadCollections()
    }
  }, [isCurrentMode, loaded, reloadCollections])

  useEffect(() => {
    if (mode) {
      if (prevMode !== CURRENT_MODE) setModeState(mode)
      else if (prevMode === CURRENT_MODE && mode !== CURRENT_MODE) setModeState('')
    }
  }, [mode, prevMode])

  const [DUMMYfilterValue, DUMMYsetFilterValue] = useState<string>('trending')
  const handleFilterChange = (newValue: any) => {
    DUMMYsetFilterValue(newValue)
  }

  if (!isCurrentMode && !isCurrentModeState) return null

  return (
    <Fragment>
      <div
        className={twMerge(
          'mx-auto',
          !isCurrentMode && 'hidden',
          !isMediumscreenAbove && styles.PaddingTopResponsive,
          styles.ExplorePageContainer
        )}
      >
        <PageTitle
          className="mb-7"
          title="Collections"
          subtitle="A group of images curated by the community to use for your next Playform project"
        />

        <FloatingActions />

        <div className="flex">
          <div className="flex w-1/100 min-w-0 flex-auto">
            <InputSearch
              className={clsx('w-full', styles.PageInputSearch)}
              value={search}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                updateSearch(event?.target?.value)
              }}
              loading={Boolean(collectionListLoading && search)}
              placeholder="Search for collections"
              size="small"
            />

            {search && !shouldShowNoResult ? (
              <Button
                className="ml-2"
                onClick={() => updateSearch('')}
                color="secondary"
                size="small"
              >
                Clear
              </Button>
            ) : null}
          </div>
          {/* NOT-IMPLEMENTED: Wait for the backend ready} */}
          {isSmallscreenAbove && false ? (
            <TabsOne value={DUMMYfilterValue} onChange={handleFilterChange}>
              <TabOne value="trending" label="Trending" />
              <TabOne value="recent" label="Recent" />
            </TabsOne>
          ) : null}
        </div>

        {hasExplorePageCollectionList ? (
          <FixedSizeList
            loadingPosition={isGridInViewport ? 'fixed' : 'absolute'}
            config={GRID_CONFIG}
            classes={classes}
            scrollRef={scrollRef}
            dataLength={explorePageCollectionListLength}
            renderItem={renderItem}
            loading={Boolean(collectionListLoading)}
            onEndOfGrid={loadMoreCollection}
            autoHeight
          />
        ) : null}

        {shouldShowNoResult ? (
          <div className={styles.EmptyContainer}>
            <Typography variant="subtitle1">
              {`There are no result for "${listParams.search}" keyword`}
            </Typography>

            <Button onClick={() => updateSearch('')} color="secondary">
              RESET SEARCH
            </Button>
          </div>
        ) : null}

        {collectionListLoading && !hasExplorePageCollectionList && !showSearch ? (
          <div className={clsx('text-center', styles.ListLoadingContainer)}>
            <CircularLoading loading disableShrink />
          </div>
        ) : null}
      </div>

      {!currentPostId && mode === CURRENT_MODE ? (
        <CollectionEditorPanel
          delay={600}
          source={'explore_collections'}
          onClickBack={onClickBack}
          collectionId={currentCollectionId || 0}
        />
      ) : null}
    </Fragment>
  )
}

export default connect(mapStateToProps, mapDispatchToProps)(CollectionList)
